import { Link, useParams } from "react-router-dom";
import { ContextBar } from "../components/ContextBar";
import { ROUTES } from "../lib/routes";
import { COMMITTEES, getCommittee } from "../lib/committees";
import { useScrollPastHero } from "../lib/useScrollPastHero";

export default function ComplianceCommittee() {
  const { slug = "" } = useParams<{ slug: string }>();
  const c = getCommittee(slug);
  const [heroRef, scrolledPast] = useScrollPastHero<HTMLElement>();

  if (!c) {
    return (
      <section className="center-hero">
        <div className="container">
          <div className="ch-eyebrow">404 · Committee</div>
          <h1 className="ch-name">Committee not found.</h1>
          <p style={{ color: "rgba(255,255,255,0.7)" }}>
            <Link to={ROUTES.compliance} style={{ color: "var(--gold-400)" }}>← Back to compliance</Link>
          </p>
        </div>
      </section>
    );
  }

  const others = COMMITTEES.filter((x) => x.slug !== c.slug).slice(0, 3);

  return (
    <article>
      <ContextBar
        visible={scrolledPast}
        tag="STATUTORY COMMITTEE"
        title={c.name}
        back={{ label: "All committees", to: ROUTES.compliance }}
      />

      <section className="center-hero" ref={heroRef}>
        <div className="container">
          <nav className="event-crumbs" aria-label="Breadcrumb">
            <Link to={ROUTES.home}>Home</Link>
            <span aria-hidden>/</span>
            <Link to={ROUTES.compliance}>Compliance</Link>
            <span aria-hidden>/</span>
            <span>Committees</span>
          </nav>
          <div className="ch-eyebrow">Mandate · {c.mandate}</div>
          <span className="ch-short">{c.anonymous ? "ANONYMOUS REPORTING" : "CONFIDENTIAL"}</span>
          <h1 className="ch-name">{c.name}</h1>
          <p className="ch-summary">{c.purpose}</p>

          <div className="ch-stats">
            <div>
              <div className="num">{c.fullMembers.length}</div>
              <div className="lbl">Members</div>
            </div>
            <div>
              <div className="num">{c.charter.length}</div>
              <div className="lbl">Charter items</div>
            </div>
            <div>
              <div className="num">{c.activity.length}</div>
              <div className="lbl">Recent actions</div>
            </div>
          </div>
        </div>
      </section>

      <section className="detail-shell">
        <div className="container">
          <div className="detail-shell-grid">
            <div className="detail-body">
              <h2>Charter</h2>
              <ul className="detail-list">
                {c.charter.map((ch, i) => <li key={i}>{ch}</li>)}
              </ul>

              <h2>Membership</h2>
              <div className="club-leadership">
                {c.fullMembers.map((m, i) => (
                  <div key={i} className="cl-cell">
                    <div className="cl-role">{m.role}</div>
                    <div className="cl-name">{m.name}</div>
                    {m.affiliation && (
                      <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, letterSpacing: "0.06em", color: "var(--text-muted)", marginTop: 4 }}>
                        {m.affiliation}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <h2 style={{ marginTop: "var(--space-6)" }}>How to report or file a complaint</h2>
              <ol className="event-schedule">
                {c.reporting.map((r, i) => (
                  <li key={i}>
                    <span className="es-time">Step {i + 1}</span>
                    <span className="es-line" aria-hidden />
                    <span className="es-body"><span className="es-what">{r}</span></span>
                  </li>
                ))}
              </ol>

              <h2>Recent activity</h2>
              <ul className="alum-journey">
                {c.activity.map((a, i) => (
                  <li key={i}>
                    <div className="yr">{a.date}</div>
                    <div className="tx">{a.action}</div>
                  </li>
                ))}
              </ul>
            </div>

            <aside className="detail-meta-card is-accented">
              <div className="dmc-block">
                <div className="dmc-label">Mandate</div>
                <div className="dmc-value">{c.mandate}</div>
              </div>
              <div className="dmc-block">
                <div className="dmc-label">Chair</div>
                <div className="dmc-value">{c.chair}</div>
              </div>
              <div className="dmc-block">
                <div className="dmc-label">Convener</div>
                <div className="dmc-value">{c.convener}</div>
              </div>
              <div className="dmc-block">
                <div className="dmc-label">Email</div>
                <div className="dmc-value"><a href={`mailto:${c.contact.email}`}>{c.contact.email}</a></div>
              </div>
              <div className="dmc-block">
                <div className="dmc-label">Phone</div>
                <div className="dmc-value">{c.contact.phone}</div>
              </div>
              {c.contact.helpline && (
                <div className="dmc-block">
                  <div className="dmc-label">National helpline</div>
                  <div className="dmc-value">{c.contact.helpline}</div>
                </div>
              )}
              <div className="dmc-block">
                <div className="dmc-label">Anonymous reporting</div>
                <div className="dmc-value">{c.anonymous ? "Yes — accepted" : "Confidential, identified"}</div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {others.length > 0 && (
        <section className="event-related">
          <div className="container">
            <div className="er-head">
              <div className="kicker">More committees</div>
              <h2>Other statutory bodies</h2>
            </div>
            <div className="er-grid">
              {others.map((o) => (
                <Link key={o.slug} to={`${ROUTES.committees}/${o.slug}`} className="er-card">
                  <div className="er-date">
                    <span className="er-day">{o.fullMembers.length}</span>
                    <span className="er-month">MEMBERS</span>
                  </div>
                  <div className="er-body">
                    <span className="er-tag">{o.mandate.split(" · ")[0]}</span>
                    <h3>{o.name}</h3>
                    <span className="er-venue">Convener · {o.convener}</span>
                  </div>
                  <span className="er-arr" aria-hidden>→</span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </article>
  );
}
